
import '../styles/globals.css'
import '@nuport/file-import/styles.css'
require('../styles/antd.less')
import initAuth from '../config/initAuth'
import initMaps from '../config/initMaps'
import initFirebase from '../config/initFirebase'
import { wrapper } from '../store'
import { useEffect, useState } from 'react'
import 'firebase/firestore'
import firebase from 'firebase/app'
import Router from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPermissions, fetchUserProfile, setAuthUser, setCompanyId, setPlan, setRole } from '../store/auth/actions'
import { setUnreadCount, addNotification, setCount, updateNotification } from '../store/notifications/actions'
import { Companies, DeliveryPlans, Notifications } from '../services/api/firebase'
import { notification, message, Spin, Modal } from 'antd'
import axios from 'axios'
import FullPageLoader from '../components/full-page-loader'
import { LoadingOutlined } from '@ant-design/icons'
import { getNotificationArgs } from '../components/notifications-tray'
import { fetchDeliveryPlanDetails, fetchDeliveryPlans, updateDeliveryPlanLoadState } from '../store/delivery-plans/actions'
import useInterval from '../hooks/useInterval'
import { DELIVERY_PLAN_PENDING_STATUS } from './delivery-plans'
import { claimsRoles } from '../utils/constants'
import { Users } from '../services/api/users'
import {
	beginSessionHandshake,
	clearSessionId,
	endSession,
	endSessionHandshake,
	getDeviceLabel,
	getSessionAgent,
	getSessionId,
	isSessionHandshake,
	recordSessionEnded,
	SESSION_CONFLICT_MESSAGE,
	SESSION_EXISTS_CODE,
	setSessionEstablished,
	setSessionId
} from '../utils/session'
import { resetActivity, startIdleSession, stopIdleSession, watchActivity } from '../utils/idle-session'

initAuth()
initMaps()
initFirebase()

const App = ({ Component, pageProps }) => {
	const dispatch = useDispatch()
	const { role, userProfile, authUser, isPermissionsLoaded } = useSelector(state => state.authReducer)
	const { unreadNotificationsMap } = useSelector(state => state.notificationsReducer)
	const [showRefreshPrompt, setShowRefreshPrompt] = useState(false)

	useEffect(() => {
		watchActivity(Router.events)
		fetchAuthUser()
	}, [])

	useEffect(() => {
		if (showRefreshPrompt) {
			message.info({
				content: <Message onClick={() => window.location.reload()} message={'A new version of the application has been deployed. Please refresh the application by clicking here.'} />,
				duration: 0
			})
		}
	}, [showRefreshPrompt])

	useEffect(() => {
		let unsubscribe
		let unsubscribeCompany
		if (authUser) {
			(async () => {
				const tokenResult = await authUser.getIdTokenResult()
				const companyId = tokenResult.claims.cid
				const plan = tokenResult.claims.plan
				unsubscribe = getDeliveryPlans(companyId)
				unsubscribeCompany = getCompany(companyId, plan)
			})()
		}
		return () => {
			if (unsubscribe) {
				const { unsubscribeDeliveryPlans, unsubscribeLoadingDeliveryPlans } = unsubscribe
				if (unsubscribeDeliveryPlans) {
					unsubscribeDeliveryPlans()
				}
				if (unsubscribeLoadingDeliveryPlans) {
					unsubscribeLoadingDeliveryPlans()
				}
			}
			if (unsubscribeCompany) {
				unsubscribeCompany()
			}
		}
	}, [authUser])

	useEffect(() => {
		let unsubscribe
		if (authUser) {
			const uid = authUser.uid
			unsubscribe = getUserNotifications(uid)
		}
		return () => {
			if (unsubscribe) {
				const { unsubscribeNotificationCount, unsubscribeNotifications } = unsubscribe
				if (unsubscribeNotificationCount) {
					unsubscribeNotificationCount()
				}
				if (unsubscribeNotifications) {
					unsubscribeNotifications()
				}
			}
		}
	}, [authUser])

	useInterval(async () => {
		try {
			const { buildId } = (await axios.get('/api/build-id')).data
			if (buildId && process.env.BUILD_ID && buildId !== process.env.BUILD_ID) {
				// A new version of the application has been deployed. Prompt user to refresh.
				if (!showRefreshPrompt) {
					setShowRefreshPrompt(true)
				}
			}
		} catch (e) {
			console.error(e)
		}
	}, 300000)


	const isSSO = authUser => {
		return authUser.providerData.some(provider => provider.providerId === 'saml.azure-sso')
	}

	/**
	 * Claims this device's session. Returns false when the user must resolve a conflict first, in
	 * which case sign-in stops here until they answer the dialog.
	 */
	const claimSession = async () => {
		beginSessionHandshake()
		try {
			const previousSessionId = getSessionId()
			const { data } = await Users.claimSession({
				agent: getSessionAgent(),
				deviceLabel: getDeviceLabel()
			})
			setSessionId(data.sessionId)
			if (data.sessionId !== previousSessionId) {
				resetActivity()
			}
			if (data.customToken) {
				// The session this user held had gone idle and was cleared out of the way, which
				// revoked the token this device signed in with moments ago. The custom token is a
				// fresh authentication; start-up resumes when it lands and the claim runs again.
				await firebase.auth().signInWithCustomToken(data.customToken)
				return false
			}
			// The server decides whether this session times out and after how long. For sessions
			// that do not, this does nothing.
			startIdleSession({
				timeoutMs: data.idleTimeoutMs,
				onReport: () => Users.reportSessionActivity(),
				onExpire: () => Users.expireSession()
			})
			// Sign-in is only now complete. The sign-in screen waits on this before handing the
			// user over to the application, so a conflict is resolved there rather than over a
			// dashboard belonging to a session this device has not been granted.
			setSessionEstablished(true)
			return true
		} catch (e) {
			if (e.code === SESSION_EXISTS_CODE) {
				// A device that already held a session and is now told the session is held
				// elsewhere is the device that was displaced. It is signed out with the reason
				// rather than being offered the chance to take back a session it just lost, which
				// is how two devices would otherwise pull it from each other indefinitely.
				if (getSessionId()) {
					endSession('session-terminated')
					return false
				}
				showSessionConflict(e.existingSession)
				return false
			}
			notification.error({
				message: 'Unable To Start Session',
				description: e.message,
				placement: 'bottomLeft'
			})
			// As above: the claim failed, so this device holds no session to be displaced from.
			clearSessionId()
			await firebase.auth().signOut()
			return false
		} finally {
			endSessionHandshake()
		}
	}

	const showSessionConflict = existingSession => {
		const heldBy = existingSession?.deviceLabel || existingSession?.agent
		Modal.confirm({
			// The message is fixed by the ticket, so it is rendered verbatim and on its own. The
			// device description from the conflict response goes in the title instead.
			title: heldBy ? `Already Signed In On ${heldBy}` : 'Already Signed In',
			content: SESSION_CONFLICT_MESSAGE,
			okText: 'Continue',
			cancelText: 'Cancel',
			// Neither choice may be skipped: dismissing the dialog unanswered would strand the user
			// authenticated with Firebase but holding no session.
			keyboard: false,
			maskClosable: false,
			onOk: async () => {
				try {
					beginSessionHandshake()
					const { data } = await Users.forceSession({
						agent: getSessionAgent(),
						deviceLabel: getDeviceLabel()
					})
					// Stored before re-authenticating so the claim that runs again on the new token
					// recognises this session as its own rather than conflicting with it.
					setSessionId(data.sessionId)
					resetActivity()
					// Displacing the other device revokes every token for this user, including the
					// one this device holds. Signing in with the custom token is a fresh
					// authentication, and the only way this device survives the displacement it
					// just caused.
					await firebase.auth().signInWithCustomToken(data.customToken)
				} catch (e) {
					clearSessionId()
					notification.error({
						message: 'Unable To Start Session',
						description: e.message,
						placement: 'bottomLeft'
					})
					await firebase.auth().signOut()
				} finally {
					endSessionHandshake()
				}
			},
			onCancel: async () => {
				// The existing session is left completely untouched.
				clearSessionId()
				await firebase.auth().signOut()
			}
		})
	}

	const fetchAuthUser = () => {
		firebase.auth().onIdTokenChanged(async authUser => {
			if (authUser) {
				if (isSSO(authUser)) {
					try {
						await Users.sso()
					} catch (e) {
						notification.error({
							message: 'Unable To Sign In Using SSO',
							description: e.message,
							placement: 'bottomLeft'
						})
						// Dropped before signing out, so this refusal is not mistaken for the
						// session having been taken by another device.
						clearSessionId()
						firebase.auth().signOut()
					}
				} else {
					// Track login for non-SSO users
					try {
						await Users.trackLogin()
					} catch (e) {
						notification.error({
							message: 'Unable To Track Login',
							description: e.message,
							placement: 'bottomLeft'
						})
					}
				}
				// A user may hold one session across the whole platform. Claiming it before the
				// rest of the app starts up means a device that loses the challenge never gets as
				// far as loading data it is not entitled to.
				const claimed = await claimSession()
				if (!claimed) return
				const tokenResult = await authUser.getIdTokenResult()
				if (tokenResult.claims) {
					dispatch(setRole(tokenResult.claims.role))
					const plan = tokenResult.claims.plan
					const companyId = tokenResult.claims.cid
					dispatch(setPlan(plan))
					dispatch(setCompanyId(companyId))
					if (plan === 'expired') {
						Modal.error({
							title: 'Your Plan has Expired',
							content: 'Please check your subscription in order to continue using Nuport.'
						})
					}
				}
				dispatch(setAuthUser(authUser))
				dispatch(fetchUserProfile())
				dispatch(fetchPermissions())
			} else {
				// Signed out, here or in another tab — including a sign-in abandoned at the
				// conflict dialog. There is no session left to time out, and nothing for the
				// sign-in screen to hand over to.
				//
				// Firebase also signs a user out by itself, without any request of ours failing
				// first: another device claiming the session revokes this user's tokens, so the
				// next token refresh — at start-up, which is to say on a plain page refresh —
				// fails and ends the session here before the application has done anything at
				// all. Every sign-out this application performs clears the session id before it
				// signs out, so one still being held is exactly that case, and the reason is
				// recorded here rather than leaving the guards to reach sign-in with nothing to
				// say. A device re-authenticating through a token it had revoked itself, by
				// taking the session from another device, is excluded by the handshake — it is
				// mid-sign-in, not displaced.
				if (getSessionId() && !isSessionHandshake()) {
					clearSessionId()
					recordSessionEnded('session-terminated')
				}
				setSessionEstablished(false)
				stopIdleSession()
			}
		})
	}

	const getUserNotifications = uid => {
		const unsubscribeNotificationCount = Notifications.listenToNotificationCount(uid, data => {
			dispatch(setUnreadCount(data.unreadCount ? data.unreadCount : 0))
			dispatch(setCount(data.count ? data.count : 0))
		})
		const unsubscribeNotifications = Notifications.listenToUnreadNotifications(
			uid,
			notification => {
				if (!unreadNotificationsMap[notification.id]) {
					showNotification(notification)
					dispatch(addNotification(notification))
				}
			},
			notification => {
				showNotification(notification)
				dispatch(updateNotification(notification))
			})
		return { unsubscribeNotificationCount, unsubscribeNotifications }
	}

	const getCompany = (cid, plan) => {
		if (!cid) return
		return Companies.listenToCompany(cid, company => {
			const companyDetails = company.companyDetails
			if (companyDetails && plan) {
				if (companyDetails.subscriptionPlan?.toLowerCase() !== plan) {
					dispatch(setPlan(companyDetails.subscriptionPlan.toLowerCase()))
				}
			}
		})
	}

	const getDeliveryPlans = cid => {
		if (!cid) return
		const unsubscribeDeliveryPlans = DeliveryPlans.listenToDeliveryPlans(cid, () => {
			dispatch(fetchDeliveryPlans(undefined, 0))
			dispatch(fetchDeliveryPlans(DELIVERY_PLAN_PENDING_STATUS, 0))
		})
		const unsubscribeLoadingDeliveryPlans = DeliveryPlans.listenToLoadingDeliveryPlans(
			cid,
			createdDeliveryPlan => {
				showDeliveryPlanLoadingNotification(createdDeliveryPlan)
				dispatch(updateDeliveryPlanLoadState(createdDeliveryPlan.id, true))
			},
			updatedDeliveryPlan => {
				showDeliveryPlanLoadingNotification(updatedDeliveryPlan)
				dispatch(updateDeliveryPlanLoadState(updatedDeliveryPlan.id, true))
			},
			removedDeliveryPlan => {
				notification.close(removedDeliveryPlan.id)
				dispatch(updateDeliveryPlanLoadState(removedDeliveryPlan.id, false))
				dispatch(fetchDeliveryPlanDetails(removedDeliveryPlan.id))
			}
		)
		return { unsubscribeDeliveryPlans, unsubscribeLoadingDeliveryPlans }
	}

	const showDeliveryPlanLoadingNotification = (deliveryPlan) => {
		notification.open({
			key: deliveryPlan.id,
			message: 'Processing Delivery Plan',
			description:
			<div style={{ display: 'flex' }}>
				<Spin
					style={{ marginRight: 12 }}
					indicator={<LoadingOutlined />}
				/>
				<p style={{ display: 'flex', alignItems: 'center', marginBottom: 0 }}><span style={{ color: '#278EA5' }}>DP-{deliveryPlan.shortId} </span>&nbsp;{' is currently being updated.'}</p>
			</div>,
			placement: 'bottomLeft',
			duration: 0
		})
	}

	const showNotification = (notificationData) => {
		notification.open(getNotificationArgs(notificationData))
	}

	return (
		// Pages check permissions once when they mount and do not look again, so hold them back
		// until permissions have arrived; otherwise a page opened directly can stay empty.
		role && ((role !== claimsRoles.ADMIN && !userProfile) || !isPermissionsLoaded) ?
			<FullPageLoader /> :
			<Component {...pageProps} />
	)
}

const Message = ({ message, onClick }) => {
	return (
		<div
			onClick={onClick}
			style={{ cursor: 'pointer', padding: '0 12px' }}
		>
			{message}
		</div>
	)
}

export default wrapper.withRedux(App)
